import { Typography, SvgIcon, CircularProgress } from '@mui/material'
import React, {
  Fragment,
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import tw, { css } from 'twin.macro'
import { LandingPageMajor, RiskMinor } from '@shopify/polaris-icons'
import { showToast } from 'src/utils/toast'
import { convertFileIfHeic } from 'src/utils/convertHeicToJpeg'
import {
  trackFilePickerCancelled,
  trackFilePickerFileSelected,
  trackFilePickerOpened,
} from 'src/utils/filePickerTracking'

interface Props {
  acceptTypes: Array<string>
  isLoading?: boolean
  fileSizeLimit?: number
  onSelect: (file: File) => void
  inputDataTestId?: string
  highlightEmptyError?: boolean
}

// TODO: make default file input from this component instead of CSV input
const FileUploadInputComponent = ({
  acceptTypes,
  isLoading,
  fileSizeLimit,
  onSelect,
  inputDataTestId,
  highlightEmptyError,
}: Props) => {
  const [isDragOver, setIsDragOver] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const inputId = inputDataTestId ?? 'banner-file'
  const inputRef = useRef<HTMLInputElement>(null)

  const onInputImage = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files?.length) {
        trackFilePickerCancelled(inputId)

        return
      }

      let file = event.target.files[0]

      trackFilePickerFileSelected(inputId, file)
      setError(null)

      try {
        file = await convertFileIfHeic(file)
      } catch {
        event.target.value = ''
        showToast('Could not read this file. Please try a different one.', { variant: 'error' })
        setError('Please upload required file.')

        return
      }

      if (!file.type) {
        event.target.value = ''
        showToast('File type could not be determined. Please try a different file.', {
          variant: 'error',
        })
        setError('Please upload required file.')

        return
      }

      if (file.size === 0) {
        event.target.value = ''
        showToast('The selected file appears to be empty. Please try a different file.', {
          variant: 'error',
        })
        setError('Please upload required file.')

        return
      }

      if (fileSizeLimit && file.size > fileSizeLimit) {
        event.target.value = ''

        showToast(`File size is bigger than ${Math.round(fileSizeLimit / 1000000)}MB`, {
          variant: 'error',
        })

        setError('Please upload required file.')

        return
      }

      if (!acceptTypes.includes(file.type)) {
        event.target.value = ''

        showToast('File type is not supported', {
          variant: 'error',
        })

        setError('Please upload required file.')

        return
      }

      setError(null)
      onSelect(file)

      event.target.value = ''
    },
    [acceptTypes, fileSizeLimit, inputId, onSelect],
  )

  const handleDragEnter = useCallback(() => {
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  const inputAcceptTypes = useMemo(() => {
    return acceptTypes.join(',')
  }, [acceptTypes])

  const handleInputClick = useCallback(() => {
    trackFilePickerOpened(inputId, inputAcceptTypes)
  }, [inputId, inputAcceptTypes])

  // React 18 has no onCancel for inputs, so listen to the native event (Chrome 113+).
  useEffect(() => {
    const input = inputRef.current

    if (!input) {
      return
    }

    const handleCancel = () => {
      trackFilePickerCancelled(inputId)
    }

    input.addEventListener('cancel', handleCancel)

    return () => {
      input.removeEventListener('cancel', handleCancel)
    }
  }, [inputId, isLoading])

  return (
    <Fragment>
      <label htmlFor={inputId}>
        <div
          css={[
            tw`relative border rounded-lg px-4 py-8 flex flex-1 flex-col justify-center items-center transition duration-300 ease-linear`,
            !(highlightEmptyError && !error) && tw`border-dashed`,
            styles.container(isDragOver, error),
            highlightEmptyError &&
              !error &&
              css`
                border-radius: 8px;
                border: 1px solid var(--Colors-Red-500, #ff1a51);
                background: var(--Basics-White, var(--mui-shades-0));
              `,
          ]}
        >
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Fragment>
              <SvgIcon css={tw`w-5 h-5`}>
                <LandingPageMajor css={tw`text-shades-1000`} />
              </SvgIcon>
              <Typography css={tw`my-2`} component="span">
                <Typography css={tw`underline text-primary-500`} component="span">
                  Tap to upload
                </Typography>{' '}
                or drag and drop
              </Typography>
              <Typography variant="body2" css={tw`text-shades-400`}>
                {`PNG, JPG, HEIC or PDF${
                  fileSizeLimit ? ` (max. ${Math.round(fileSizeLimit / 1000000)}MB)` : ''
                }`}
              </Typography>
              <input
                ref={inputRef}
                type="file"
                id={inputId}
                name="file"
                accept={inputAcceptTypes}
                onClick={handleInputClick}
                onChange={onInputImage}
                css={styles.input}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                {...(inputDataTestId ? { 'data-testid': inputDataTestId } : {})}
              />
            </Fragment>
          )}
        </div>
      </label>
      {error && (
        <div css={tw`mt-2 flex items-center gap-1`}>
          <RiskMinor width={20} css={tw`fill-critical-500`} />
          <Typography variant="body2" css={tw`text-critical-500`}>
            {error}
          </Typography>
        </div>
      )}
    </Fragment>
  )
}

const styles = {
  container: (isDragOver?: boolean, error?: string | null) => [
    isDragOver
      ? css`
          background-color: var(--mui-shades-50);
          border-color: var(--mui-primary-500);
        `
      : css`
          background-color: var(--mui-shades-0);
        `,
    error &&
      css`
        border-color: var(--mui-critical-500);
        border-style: solid;
      `,
  ],
  input: [
    css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
      z-index: 1;
      appearance: none;
      -webkit-appearance: none;
      opacity: 0;
    `,
  ],
}

export const FileUploadInput = memo(FileUploadInputComponent)
